import { createContext, useContext } from "react";
import type * as T from "./types";

/** Erro da API com mensagem apresentável ao usuário. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/**
 * Contrato da console com a API pública v1. Cada método corresponde a um
 * operationId em api/openapi/v1.yaml. A implementação HTTP e a simulada
 * seguem a mesma interface.
 */
export interface ApiClient {
  login(email: string, password: string): Promise<T.LoginChallenge>;
  verifyTotp(challengeId: string, code: string): Promise<T.Session>;
  logout(): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  confirmPasswordReset(input: { token: string; newPassword: string; code: string }): Promise<void>;
  getSession(): Promise<T.Session>;
  /** Condições do teste gratuito (ADR 0038): vaga, limites do pool de teste e versão dos termos. */
  getTrialInfo(): Promise<T.TrialInfo>;
  /** Cadastra um teste; o link para criar senha e 2FA vai por e-mail. */
  trialSignup(input: T.TrialSignup): Promise<void>;
  updatePreferences(input: T.Preferences): Promise<T.Preferences>;
  changePassword(input: { currentPassword: string; newPassword: string; code: string }): Promise<void>;
  updateProfile(input: T.Profile): Promise<T.Profile>;
  /** pending: link enviado ao endereço novo; changed: servidor sem e-mail trocou na hora (ADR 0020). */
  changeEmail(input: { newEmail: string; currentPassword: string; code: string }): Promise<T.EmailChange>;
  confirmEmailChange(token: string): Promise<T.EmailChange>;

  lookupInvitation(token: string): Promise<T.InvitationInfo>;
  beginInvitationSignup(input: { token: string; name: string; password: string }): Promise<T.TotpSetup>;
  activateInvitation(input: { token: string; code: string }): Promise<T.Session>;
  joinInvitation(token: string): Promise<{ orgId: string }>;

  listOrganizations(): Promise<T.Organization[]>;
  getOverview(orgId: string): Promise<T.Overview>;

  listMachines(orgId: string, filter?: { status?: T.ProtectionStatus; groupId?: string; q?: string }): Promise<T.Machine[]>;
  getMachine(orgId: string, machineId: string): Promise<T.MachineDetail>;
  startBackup(orgId: string, machineId: string): Promise<T.Job>;
  /** Imagem de disco imediata; disks vazio usa os discos da política (ADR 0022). */
  startImage(orgId: string, machineId: string, disks: string[]): Promise<T.Job>;
  /** Discos que o agente informou na última sessão; vazio onde imagem não se aplica. */
  listDisks(orgId: string, machineId: string): Promise<T.MachineDisks>;
  /** Instâncias de banco da máquina (ADR 0023). */
  listDatabases(orgId: string, machineId: string): Promise<T.DatabaseInstance[]>;
  /** Motores que o agente achou na máquina na última sessão. */
  foundDatabases(orgId: string, machineId: string): Promise<T.FoundDatabases>;
  createDatabase(orgId: string, machineId: string, input: T.DatabaseInput): Promise<T.DatabaseInstance>;
  updateDatabase(orgId: string, machineId: string, databaseId: string, input: T.DatabaseInput): Promise<T.DatabaseInstance>;
  deleteDatabase(orgId: string, machineId: string, databaseId: string): Promise<void>;
  /** Pede ao agente que confira conexão, ferramenta e privilégio; o resultado chega em lastTest. */
  testDatabase(orgId: string, machineId: string, databaseId: string): Promise<T.Job>;
  startDatabaseBackup(orgId: string, machineId: string, databaseId: string): Promise<T.Job>;
  /** Compartilhamentos SMB da máquina (ADR 0030). */
  listShares(orgId: string, machineId: string): Promise<T.Share[]>;
  createShare(orgId: string, machineId: string, input: T.ShareInput): Promise<T.Share>;
  updateShare(orgId: string, machineId: string, shareId: string, input: T.ShareInput): Promise<T.Share>;
  deleteShare(orgId: string, machineId: string, shareId: string): Promise<void>;
  testShare(orgId: string, machineId: string, shareId: string): Promise<T.Job>;
  startShareBackup(orgId: string, machineId: string, shareId: string): Promise<T.Job>;
  /** Catálogo de credenciais da organização (ADR 0030); só administrador. */
  listCredentials(orgId: string): Promise<T.Credential[]>;
  createCredential(orgId: string, input: T.CredentialInput): Promise<T.Credential>;
  updateCredential(orgId: string, credentialId: string, input: T.CredentialInput): Promise<T.Credential>;
  deleteCredential(orgId: string, credentialId: string): Promise<void>;
  revokeMachine(orgId: string, machineId: string): Promise<T.Machine>;
  /** imageDisks: discos marcados para a imagem na agenda da política (ADR 0033); ausente mantém. */
  updateMachine(orgId: string, machineId: string, input: { groupId?: string; policyId?: string; imageDisks?: string[] }): Promise<T.MachineDetail>;
  listSnapshots(orgId: string, machineId: string): Promise<T.Snapshot[]>;
  /** Relatório do ponto parcial (ADR 0032): total, grupos por pasta e erro e a lista guardada. */
  snapshotErrors(orgId: string, snapshotId: string): Promise<T.SnapshotErrors>;
  browseSnapshot(orgId: string, snapshotId: string, path: string): Promise<T.FileEntry[]>;
  /** Catálogo (ADR 0036): itens pelo trecho do nome em todos os pontos da máquina, e em quais pontos um item está. */
  searchCatalog(orgId: string, machineId: string, q: string): Promise<T.CatalogHit[]>;
  catalogOccurrences(orgId: string, machineId: string, dirObjectId: string, name: string): Promise<T.CatalogOccurrences>;
  createRestore(orgId: string, req: T.RestoreRequest): Promise<T.Job>;

  listGroups(orgId: string): Promise<T.MachineGroup[]>;
  listPolicies(orgId: string): Promise<T.Policy[]>;
  createPolicy(orgId: string, input: T.PolicyInput): Promise<T.Policy>;
  updatePolicy(orgId: string, policyId: string, input: T.PolicyInput): Promise<T.Policy>;
  getUsage(orgId: string): Promise<T.Usage>;
  /** Armazenamento contratado como o cliente vê: nome e volume, sem infraestrutura (ADR 0021). */
  storage(orgId: string): Promise<T.StorageSummary>;

  // Administração da plataforma (ADR 0021): exigem administrador da plataforma
  // com sessão; o cliente não enxerga nenhuma destas rotas.
  adminListPools(): Promise<T.StoragePool[]>;
  adminCreatePool(input: T.PoolInput): Promise<T.StoragePool>;
  adminUpdatePool(poolId: string, input: T.PoolInput): Promise<T.StoragePool>;
  adminDeletePool(poolId: string): Promise<void>;
  adminTestPool(input: T.PoolInput & { poolId?: string }): Promise<{ ok: boolean }>;
  adminSftpHostKey(host: string, port: number): Promise<{ knownHosts: string; fingerprint: string; keyType: string }>;
  /** Certificado que um servidor HTTPS (WebDAV) apresenta, para fixar pela impressão digital (ADR 0034). */
  adminTLSCertificate(url: string): Promise<T.TLSCertificateInfo>;
  adminSetPoolReplica(poolId: string, replicaPoolId: string | null): Promise<T.StoragePool>;
  /** Mede capacidade e espaço livre no destino; nulo onde o backend não informa. */
  adminProbePool(poolId: string): Promise<T.PoolCapacity>;
  /** Cadastro de organizações: do administrador da plataforma, como os pools (ADR 0021). */
  adminCreateOrg(input: T.OrganizationInput): Promise<T.OrganizationDetail>;
  /**
   * O nome, e no teste gratuito o fim (ADR 0038): data prorroga, null converte em cliente, ausente mantém.
   * A quota mora na atribuição de armazenamento (ADR 0037).
   */
  adminUpdateOrg(orgId: string, input: { name: string; trialEndsAt?: string | null }): Promise<T.OrganizationDetail>;
  /** Encerra a organização (ADR 0024): some da console e os dados ficam 30 dias, prazo para reabrir. */
  adminCloseOrg(orgId: string): Promise<T.OrganizationDetail>;
  adminReopenOrg(orgId: string): Promise<T.OrganizationDetail>;
  /** Antecipa o expurgo de uma organização encerrada: apaga os backups e o cadastro. Sem volta. */
  adminPurgeOrg(orgId: string): Promise<T.OrganizationDetail>;
  adminListOrgPools(orgId: string): Promise<T.OrgPool[]>;
  adminSetOrgPool(orgId: string, poolId: string, input: T.OrgPoolInput): Promise<T.OrgPool>;
  adminRemoveOrgPool(orgId: string, poolId: string): Promise<void>;

  listEnrollmentTokens(orgId: string): Promise<T.EnrollmentToken[]>;
  createEnrollmentToken(orgId: string, input: T.EnrollmentTokenInput): Promise<T.EnrollmentTokenCreated>;

  listActivity(orgId: string, machineId?: string): Promise<T.Job[]>;
  listAudit(orgId: string): Promise<T.AuditEvent[]>;
  /** Relatórios de evidência (ADR 0026): catálogo e emissões. A emissão em si é um download do navegador. */
  listReportKinds(orgId: string): Promise<T.ReportKind[]>;
  listIssuedReports(orgId: string): Promise<T.ReportIssue[]>;
  listAlerts(orgId: string, status?: "open" | "all"): Promise<T.Alert[]>;
  listNotificationChannels(orgId: string): Promise<{ emailAvailable: boolean; channels: T.NotificationChannel[] }>;
  createNotificationChannel(orgId: string, input: T.NotificationChannelInput): Promise<{ channel: T.NotificationChannel; secret: string | null }>;
  updateNotificationChannel(orgId: string, channelId: string, input: T.NotificationChannelInput): Promise<T.NotificationChannel>;
  deleteNotificationChannel(orgId: string, channelId: string): Promise<void>;
  testNotificationChannel(orgId: string, channelId: string): Promise<{ ok: boolean }>;
  /** Emite e envia agora o resumo de proteção do mês anterior ao canal de e-mail (ADR 0028). */
  sendMonthlySummary(orgId: string, channelId: string): Promise<{ ok: boolean }>;
  listUsers(orgId: string): Promise<T.User[]>;
  updateMember(orgId: string, userId: string, role: T.OrgRole): Promise<T.User>;
  removeMember(orgId: string, userId: string): Promise<void>;
  resetMemberTotp(orgId: string, userId: string): Promise<void>;
  listInvitations(orgId: string): Promise<T.Invitation[]>;
  createInvitation(orgId: string, input: { email: string; name: string; role: T.OrgRole }): Promise<T.InvitationCreated>;
  revokeInvitation(orgId: string, invitationId: string): Promise<void>;
  listApiTokens(orgId: string): Promise<T.ApiToken[]>;
  createApiToken(orgId: string, input: { name: string; role: T.OrgRole; expiresInDays: number }): Promise<T.ApiTokenCreated>;
  revokeApiToken(orgId: string, tokenId: string): Promise<void>;
}

export const ApiContext = createContext<ApiClient | null>(null);

export function useApi(): ApiClient {
  const api = useContext(ApiContext);
  if (!api) throw new Error("ApiContext ausente");
  return api;
}
